/**
 * API Service for Moving Motivators backend
 * Handles all communication with the Lambda backend
 */

// Use specific Moving Motivators endpoint
const MM_API_URL = import.meta.env.VITE_MM_API_URL || import.meta.env.VITE_API_URL || 'http://localhost:3001';
const API_URL = MM_API_URL.endsWith('/') ? MM_API_URL.slice(0, -1) : MM_API_URL;

function normalizeRoomId(roomId: string): string {
  return roomId.trim().toUpperCase();
}

export interface CreateRoomRequest {
  roomName: string;
  playerName: string;
  email: string;
  visibility: 'public' | 'private';
}

export interface JoinRoomRequest {
  playerName: string;
  email: string;
}

export interface UpdateCardPositionsRequest {
  playerId: string;
  cards: Array<{
    cardId: string;
    position: number;
  }>;
}

export interface Room {
  id: string;
  roomName: string;
  visibility: 'public' | 'private';
  createdBy: {
    playerName: string;
    email: string;
  };
  players: Array<{
    playerName: string;
    email: string;
  }>;
  cards: Array<{
    playerId: string;
    cardId: string;
    position: number;
  }>;
  createdAt: string;
  updatedAt: string;
}

export interface CardPosition {
  playerId: string;
  cardId: string;
  position: number;
}

export interface ParticipantRanking {
  playerName: string;
  email: string;
  ranking: string[];
}

/**
 * Create a new room
 */
export async function createRoom(request: CreateRoomRequest): Promise<Room> {
  const response = await fetch(`${API_URL}/rooms`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create room');
  }

  return response.json();
}

/**
 * Get room by ID
 */
export async function getRoom(roomId: string): Promise<Room> {
  const normalizedRoomId = normalizeRoomId(roomId);
  const response = await fetch(`${API_URL}/rooms/${normalizedRoomId}`, {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get room');
  }

  return response.json();
}

/**
 * Join an existing room
 */
export async function joinRoom(
  roomId: string,
  request: JoinRoomRequest
): Promise<Room> {
  const normalizedRoomId = normalizeRoomId(roomId);
  const response = await fetch(`${API_URL}/rooms/${normalizedRoomId}/players`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to join room');
  }

  return response.json();
}

/**
 * Update card positions for a player
 */
export async function updateCardPositions(
  roomId: string,
  request: UpdateCardPositionsRequest
): Promise<Room> {
  const normalizedRoomId = normalizeRoomId(roomId);
  const response = await fetch(`${API_URL}/rooms/${normalizedRoomId}/cards`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update card positions');
  }

  return response.json();
}

/**
 * List all public rooms
 */
export async function listPublicRooms(): Promise<Room[]> {
  const response = await fetch(`${API_URL}/rooms`, {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to list rooms');
  }

  return response.json();
}

/**
 * Get participant rankings from room card data
 */
export function getParticipantRankings(room: Room, allMotivators: string[]): ParticipantRanking[] {
  // Build map of player cards indexed by email
  const playerCardsMap = new Map<string, CardPosition[]>();

  room.players.forEach((player) => {
    playerCardsMap.set(player.email, []);
  });

  room.cards.forEach((card) => {
    const playerCards = playerCardsMap.get(card.playerId);
    if (playerCards) {
      playerCards.push(card);
    }
  });

  // Convert to ParticipantRanking format
  return room.players.map((player) => {
    const playerCards = playerCardsMap.get(player.email) || [];
    
    // Sort by position and get card IDs
    const sortedCards = playerCards
      .sort((a, b) => a.position - b.position)
      .map((card) => card.cardId);

    // If player has no cards, show default order
    // Otherwise add any missing motivators at the end
    let ranking: string[] = [];
    
    if (sortedCards.length === 0) {
      // Default ranking: all motivators in original order
      ranking = [...allMotivators];
    } else {
      // Started ranking, add any missing motivators
      ranking = [...sortedCards];
      allMotivators.forEach((motivator) => {
        if (!ranking.includes(motivator)) {
          ranking.push(motivator);
        }
      });
    }

    return {
      playerName: player.playerName,
      email: player.email,
      ranking,
    };
  });
}
